import { useState, type ReactNode } from 'react'
import { EcranDeMaintenance } from './EcranDeMaintenance'
import { useMaintenance } from './etat'

/**
 * **Le seul point de branchement de la maintenance** — [DECISIONS §196],
 * RG-9.12.
 *
 * `App.tsx` n'en connaît qu'une ligne. Tout ce qui suit — quand l'écran plein
 * prend la page, et surtout quand il ne la prend PAS — vit ici.
 *
 * ## La règle, et c'est la décision centrale de tout ce chantier
 *
 * > **La porte tranche UNE fois, à la première réponse de
 * > `GET /api/v1/maintenance`.**
 *
 * Un écran plein démonte l'arbre React. S'il tombait sur un candidat à la
 * question 34 sur 50, il détruirait lui-même ce que la coupure allait
 * détruire : `Epreuve.tsx` garde ses réponses en mémoire jusqu'à la remise
 * (RG-3.11octies), et jusqu'à quatre-vingts minutes de travail n'ont jamais
 * quitté l'appareil. Il le détruirait à coup sûr, là où la coupure laissait une
 * chance à l'onglet resté ouvert.
 *
 * Donc :
 *
 * - **maintenance déjà en cours à l'arrivée** → l'écran plein prend la page. La
 *   coque a bien été peinte entre-temps — c'est PR-2, plus bas — mais elle
 *   n'est que l'écran de démarrage : le verdict tombe à la première réponse,
 *   quelques centaines de millisecondes après l'ouverture, avant que rien
 *   n'existe en mémoire qu'un démontage puisse détruire ;
 * - **maintenance qui commence ensuite** → la porte ne bascule plus. C'est le
 *   `BandeauDeMaintenance` qui l'annonce, en haut, sans rien démonter — et
 *   c'est ce qui rend honnête sa phrase « ne ferme pas cet onglet ».
 *
 * ## Elle ne retarde pas le démarrage
 *
 * Tant que l'état n'est pas connu, **c'est la coque qui est rendue** — comme
 * `PorteDeLaVitrine` (§194) et pour la même raison : la coque lance ses
 * requêtes de démarrage en parallèle (PR-2), et attendre ici aurait resérialisé
 * ce que PR-2 venait de déparalléliser. Le sondage part en même temps que
 * `/me`, et il pèse deux cents octets.
 */
export function PorteDeMaintenance({ coque }: { coque: ReactNode }) {
  const maintenance = useMaintenance()

  /**
   * Le verdict, figé à la première réponse.
   *
   * `null` tant qu'on n'a rien appris — et c'est `connu` qui fait la
   * différence, parce que `aucune` veut dire à la fois « rien de prévu » et
   * « pas encore de réponse ».
   */
  const [laPorteEstAuNous, setLaPorteEstAuNous] = useState<boolean | null>(null)

  /**
   * L'entrée de l'équipe — RG-9.16.
   *
   * L'administrateur n'a pas encore de session : la route publique lui répond
   * `laissez_passer: false`, et il verrait donc l'écran comme un candidat, sans
   * aucun moyen de se connecter. Ce drapeau lui rend la coque, dont le
   * formulaire de connexion est la seule chose qui réponde encore. Il n'ouvre
   * AUCUN droit : le serveur ferme tout le reste, et un candidat qui le tape
   * retombe sur des écrans fermés.
   */
  const [entreeDeLEquipe, setEntreeDeLEquipe] = useState(false)

  const { connu, etat } = maintenance

  /**
   * **L'ajustement se fait PENDANT le rendu, pas dans un effet.**
   *
   * C'est le patron que React nomme lui-même « ajuster l'état quand une
   * propriété change » : le rendu est abandonné et rejoué avant que quoi que ce
   * soit ne soit peint. Un `useEffect` aurait peint la coque une image, puis
   * l'écran — un clignotement à chaque arrivée sur un site fermé.
   *
   * Il ne s'exécute qu'une fois : la garde `=== null` le rend inerte ensuite.
   */
  if (connu && laPorteEstAuNous === null) {
    setLaPorteEstAuNous(etat === 'en_cours')
  }

  /**
   * **La reprise rend la page, et pour de bon.**
   *
   * Sans cette ligne, le candidat resterait devant l'écran après la reprise. Et
   * le verdict repassé à `false` est définitif : une seconde fenêtre, ouverte
   * pendant qu'il travaille, ne reprendra pas la page en démontant son épreuve
   * — c'est ce que toute cette porte protège.
   */
  if (laPorteEstAuNous === true && etat === 'aucune') {
    setLaPorteEstAuNous(false)
  }

  // `laissez_passer` est vrai dès qu'une session éditoriale existe : celui qui
  // répare traverse son propre rideau sans qu'on ait inventé un secret.
  if (maintenance.laissezPasser || entreeDeLEquipe) return <>{coque}</>

  if (laPorteEstAuNous === true) {
    return (
      <EcranDeMaintenance
        onEntreeDeLEquipe={() => {
          setEntreeDeLEquipe(true)
        }}
      />
    )
  }

  return <>{coque}</>
}
